import React, { useMemo } from 'react';
import ResearchReportEditor from './ResearchReportEditor';
import { TokenNoteDto, TweetStatsResponse } from '../api/fe-management-system-api/types';
import TweetCard from './TweetCard';

interface NoteTabContentProps {
  tokenAddress: string;
  noteContents: string;
  isEditingContents: boolean;
  noteSource?: TokenNoteDto;
  isNoteSaving: boolean;
  handleStartEdit: () => void;
  handleSaveNote: (contents: string) => Promise<void>;
  setIsEditingContents: (value: boolean) => void;
  tweetStats: TweetStatsResponse | null;
  isLoadingTweetStats: boolean;
}

const NoteTabContent: React.FC<NoteTabContentProps> = ({
  tokenAddress,
  noteContents,
  isEditingContents,
  noteSource,
  isNoteSaving,
  handleStartEdit,
  handleSaveNote,
  setIsEditingContents,
  tweetStats,
  isLoadingTweetStats,
}) => {
  const sortedAdminModTweets = useMemo(() => {
    if (!tweetStats) return [];
    return [...tweetStats.adminTweets, ...tweetStats.moderatorTweets].sort(
      (a, b) => new Date(b.createdAtDate).getTime() - new Date(a.createdAtDate).getTime()
    );
  }, [tweetStats]);

  const sortedQuotedTweets = useMemo(() => {
    if (!tweetStats) return [];
    return [...tweetStats.quotedTweets].sort(
      (a, b) => new Date(b.createdAtDate).getTime() - new Date(a.createdAtDate).getTime()
    );
  }, [tweetStats]);

  return (
    <div className="flex flex-col gap-6">
        {tweetStats && (
        <div className="flex flex-col gap-6 border-gray-800 pt-0">
          {/* 통계 요약 바 */}
          <div className="grid grid-cols-4 gap-4">
            <div className="bg-gray-800/40 border border-gray-700/50 p-4 rounded-xl">
              <p className="text-gray-400 text-xs uppercase font-bold mb-1">Total Tweets</p>
              <p className="text-2xl font-bold text-white">{tweetStats.statistics.totalTweets.toLocaleString()}</p>
            </div>
            <div className="bg-gray-800/40 border border-gray-700/50 p-4 rounded-xl">
              <p className="text-blue-400 text-xs uppercase font-bold mb-1">Admin Tweets</p>
              <p className="text-2xl font-bold text-white">{tweetStats.statistics.adminTweets.toLocaleString()}</p>
            </div>
            <div className="bg-gray-800/40 border border-gray-700/50 p-4 rounded-xl">
              <p className="text-purple-400 text-xs uppercase font-bold mb-1">Mod Tweets</p>
              <p className="text-2xl font-bold text-white">{tweetStats.statistics.moderatorTweets.toLocaleString()}</p>
            </div>
            <div className="bg-gray-800/40 border border-gray-700/50 p-4 rounded-xl">
              <p className="text-green-400 text-xs uppercase font-bold mb-1">Unique Quotes</p>
              <p className="text-2xl font-bold text-white">{tweetStats.statistics.uniqueQuotedTweets.toLocaleString()}</p>
            </div>
          </div>
        </div>
       )}
       <ResearchReportEditor
         tokenAddress={tokenAddress}
         contents={noteContents}
         isEditing={isEditingContents}
        contentsReferencedFrom={noteSource?.contentsReferencedFrom}
        contentsReferencedTo={noteSource?.contentsReferencedTo}
        projectSiteUrl={noteSource?.projectSiteUrl}
        communityId={noteSource?.twitterCommunityId}
        onStartEdit={handleStartEdit}
        onSave={async (nextContents: string) => {
          await handleSaveNote(nextContents);
        }}
        onCancel={() => {
          setIsEditingContents(false);
        }}
        isSaving={isNoteSaving}
      />

      {/* 트윗 통계 섹션 */}
      {tweetStats && (
        <div className="flex flex-col gap-6 mt-4 border-t border-gray-800 pt-8 pb-12">

          {/* 트윗 리스트 (2단 레이아웃) */}
          <div className="grid grid-cols-2 gap-6">
            {/* 좌측: Admin/Mod Tweets */}
            <div className="flex flex-col gap-4">
              <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2 px-1">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                Admin & Moderators
              </h3>
              <div className="flex flex-col gap-3 max-h-[600px] overflow-y-auto pr-2 custom-scrollbar">
                {sortedAdminModTweets.length > 0 ? (
                  sortedAdminModTweets.map((t) => (
                    <TweetCard
                      key={t.id}
                      tweet={{
                        id: t.id,
                        text: t.text,
                        createdAt: t.createdAtDate,
                        author: t.author,
                        likeCount: t.likeCount,
                        retweetCount: t.retweetCount,
                        replyCount: t.replyCount,
                      }}
                    />
                  ))
                ) : (
                  <div className="bg-gray-800/20 border border-dashed border-gray-700 rounded-xl p-8 text-center">
                    <p className="text-gray-500 text-sm">No activity in the last 24 hours.</p>
                  </div>
                )}
              </div>
            </div>

            {/* 우측: Quoted Tweets */}
            <div className="flex flex-col gap-4">
              <h3 className="text-sm font-bold text-gray-300 flex items-center gap-2 px-1">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                Most Quoted Tweets
              </h3>
              <div className="flex flex-col gap-3 max-h-[1000px] overflow-y-auto pr-2 custom-scrollbar">
                {sortedQuotedTweets.length > 0 ? (
                  sortedQuotedTweets.map((t) => (
                    <TweetCard
                      key={t.id}
                      tweet={{
                        id: t.id,
                        text: t.text,
                        createdAt: t.createdAtDate,
                        author: t.author,
                        likeCount: t.likeCount,
                        retweetCount: t.retweetCount,
                        replyCount: t.replyCount,
                      }}
                    />
                  ))
                ) : (
                  <div className="bg-gray-800/20 border border-dashed border-gray-700 rounded-xl p-8 text-center">
                    <p className="text-gray-500 text-sm">No quoted tweets in the last 24 hours.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {isLoadingTweetStats && (
        <div className="flex items-center justify-center p-12 border-t border-gray-800">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
          <span className="ml-3 text-gray-400">Loading tweet data...</span>
        </div>
      )}
    </div>
  );
};

export default NoteTabContent;
